import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, Sparkles } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useTheme } from '../../layouts/SelfServePortalLayout';

const DASHBOARD_GUIDE_VIDEO_URL = process.env.REACT_APP_DASHBOARD_GUIDE_VIDEO_URL || '';

const DashboardWelcomeCard = ({ onDismiss }) => {
  const { t } = useI18n();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [showModal, setShowModal] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setVideoPlaying(false);
  }, []);

  useEffect(() => {
    if (!showModal) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeModal();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showModal, closeModal]);

  const cardShell = isDark
    ? 'bg-emerald-950/55 backdrop-blur-xl border-emerald-500/25 shadow-[0_8px_32px_rgba(6,78,59,0.28)]'
    : 'bg-white/90 backdrop-blur-xl border-stone-200 shadow-[0_8px_30px_rgba(16,185,129,0.08)]';

  const headingClass = isDark ? 'text-white' : 'text-gray-900';
  const bodyClass = isDark ? 'text-emerald-100/80' : 'text-gray-600';

  const secondaryButtonClass = isDark
    ? 'bg-emerald-950/40 border-emerald-600/40 text-emerald-50 hover:bg-emerald-900/50 focus-visible:ring-emerald-400'
    : 'bg-white border-stone-300 text-gray-700 hover:bg-stone-50 focus-visible:ring-emerald-500';

  const videoFrameClass = isDark
    ? 'bg-gradient-to-br from-emerald-950/90 via-emerald-900/60 to-emerald-800/35 border-emerald-500/30'
    : 'bg-gradient-to-br from-white via-emerald-50/70 to-emerald-100/40 border-emerald-200/80';

  const renderVideoPlaceholder = () => (
    <button
      type="button"
      onClick={() => setVideoPlaying(true)}
      className={`relative w-full h-full min-h-[220px] flex items-center justify-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-xl overflow-hidden ${videoFrameClass}`}
      aria-label={t('dashboard.welcomeCard.watchGuide')}
    >
      <div
        className={`absolute inset-0 ${isDark ? 'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_55%)]' : 'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.14),transparent_55%)]'}`}
      />
      <div className="absolute inset-0 opacity-30 bg-[linear-gradient(135deg,transparent_20%,rgba(16,185,129,0.12)_100%)]" />
      <div className="relative w-16 h-16 rounded-full border border-emerald-400/40 bg-emerald-500/15 backdrop-blur-md flex items-center justify-center shadow-[0_8px_24px_rgba(16,185,129,0.25)] group-hover:bg-emerald-500/25 transition-colors">
        <Play size={28} className="text-emerald-500 ml-1" fill="currentColor" />
      </div>
      <span
        className={`absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
          isDark ? 'bg-emerald-950/70 text-emerald-100 border border-emerald-700/50' : 'bg-white/85 text-gray-700 border border-stone-200'
        }`}
      >
        1:45
      </span>
      {!DASHBOARD_GUIDE_VIDEO_URL && (
        <span className={`absolute bottom-3 left-3 right-16 text-xs ${bodyClass}`}>
          {t('dashboard.welcomeCard.videoComingSoon')}
        </span>
      )}
    </button>
  );

  const renderVideoContent = () => {
    if (videoPlaying && DASHBOARD_GUIDE_VIDEO_URL) {
      const isEmbed = /youtube\.com|youtu\.be|vimeo\.com/.test(DASHBOARD_GUIDE_VIDEO_URL);
      if (isEmbed) {
        return (
          <iframe
            title={t('dashboard.welcomeCard.watchGuide')}
            src={DASHBOARD_GUIDE_VIDEO_URL}
            className="w-full h-full border-0 rounded-xl"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      }
      return (
        <video
          src={DASHBOARD_GUIDE_VIDEO_URL}
          className="w-full h-full object-cover rounded-xl"
          controls
          autoPlay
        />
      );
    }
    return renderVideoPlaceholder();
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative mb-6 rounded-2xl border p-4 sm:p-5 max-w-[1200px] mx-auto ${cardShell}`}
        data-testid="dashboard-welcome-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
              <Sparkles size={18} className="text-emerald-500" />
            </div>
            <div className="min-w-0">
              <h3 className={`font-semibold text-base sm:text-lg ${headingClass}`}>
                {t('dashboard.welcomeCard.quickGuideTitle')}
              </h3>
              <p className={`text-sm mt-1 ${bodyClass}`}>
                {t('dashboard.welcomeCard.shortWalkthrough')}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              <Play size={16} fill="currentColor" />
              {t('dashboard.welcomeCard.watchGuide')}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${secondaryButtonClass}`}
            >
              {t('dashboard.welcomeCard.dismiss')}
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-emerald-950/55' : 'bg-slate-900/40'}`}
            onClick={closeModal}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              className={`relative w-full max-w-[1200px] rounded-2xl border overflow-hidden ${cardShell}`}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="dashboard-welcome-modal-title"
              data-testid="dashboard-welcome-modal"
            >
              <button
                type="button"
                onClick={closeModal}
                className={`absolute top-4 right-4 z-10 p-2 rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${secondaryButtonClass}`}
                aria-label={t('common.close')}
              >
                <X size={18} />
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-5 sm:p-6 lg:p-8 items-center">
                <div className={`rounded-2xl border p-2 ${isDark ? 'border-emerald-500/25 bg-emerald-950/35' : 'border-emerald-100 bg-emerald-50/30'}`}>
                  <div className="aspect-video w-full">{renderVideoContent()}</div>
                </div>

                <div className="flex flex-col justify-center lg:pr-4">
                  <h2 id="dashboard-welcome-modal-title" className={`text-xl sm:text-2xl font-bold mb-3 ${headingClass}`}>
                    {t('dashboard.welcomeCard.watchDashboardReady')}
                  </h2>
                  <p className={`text-sm sm:text-base mb-6 ${bodyClass}`}>
                    {t('dashboard.welcomeCard.shortWalkthrough')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={() => setVideoPlaying(true)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                    >
                      <Play size={16} fill="currentColor" />
                      {t('dashboard.welcomeCard.watchGuide')}
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className={`inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${secondaryButtonClass}`}
                    >
                      {t('dashboard.welcomeCard.dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DashboardWelcomeCard;
